import { useEffect, useState } from 'react'
import logo from '../assets/conetec-logo.svg'
import { pickSignInLang, SIGN_IN_COPY, SIGN_IN_LANGS, type SignInLang } from '../lib/signInCopy'
import {
  collectHandover,
  isHandoverCode,
  lookupHandover,
} from '../lib/shop'
import { CollectBody, type CollectPhase as Phase } from '../components/shop/CollectBody'

/**
 * #/collect/<code> — THE SCANNED PAGE. REC-49, the web half of REC-12.
 *
 * ── THE ONLY ROUTE ABOVE THE AUTH GATE ──────────────────────────────────────
 *
 * App.tsx matches this path BEFORE it looks at the session, so it renders the
 * same whether the phone is signed out, signed in, still loading, or signed in
 * as somebody with no person behind them. That is the feature: the person on
 * the shelf in Lima may hold no Conetec account (B21, the 40% constraint).
 *
 * It is also the one place a careless change opens a hole, so the rule is
 * narrow and worth keeping: THIS PAGE NEVER TOUCHES `Me`, never loads the
 * shell, and calls exactly two functions — handover_lookup and
 * handover_collect — both anon-callable, both returning no ids. Anything else
 * this page wants is a new anon surface and a new decision.
 *
 * ── SHOW, THEN CONFIRM — NEVER COLLECT ON OPEN (David, 27 Sep) ──────────────
 *
 * Opening the link reads; only the press writes. A link previewer, a
 * screenshot opened by accident, or the buyer tapping their own code must not
 * spend it. That is the magic-link lesson (Safe Links pre-fetches and spends
 * single-use links) applied to the one other single-use link in the app.
 *
 * ── THE STATE IS THE SERVER'S ───────────────────────────────────────────────
 *
 * handover_lookup already says ready / collected / expired / not_ready /
 * unknown. The screen renders the word it is given and never re-derives it
 * from a date or a status (065's single-block-reason rule).
 *
 * ── AN EXPIRED CODE IS RELEASED BY BEING SCANNED ────────────────────────────
 *
 * REC-12 rule 3: expiry is not a cron, the SCAN notices. handover_lookup is
 * STABLE and cannot write, so when it says `expired` this page calls
 * handover_collect once — which cannot collect (its UPDATE requires
 * expires_at > now()) and releases the hold and refunds the stars instead.
 * Without that, "the stars have gone back" would be a sentence that is true
 * only after the buyer next opens their orders.
 */
export function Collect({ code }: { code: string }) {
  const [lang, setLang] = useState<SignInLang>(() =>
    pickSignInLang(typeof navigator === 'undefined' ? [] : navigator.languages),
  )
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' })
  const [busy, setBusy] = useState(false)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    document.documentElement.lang = SIGN_IN_LANGS.find((l) => l.key === lang)?.htmlLang ?? 'en'
  }, [lang])

  useEffect(() => {
    let live = true
    if (!isHandoverCode(code)) {
      setPhase({ kind: 'view', justCollected: false, view: {
        state: 'unknown', itemName: null, variantLabel: null, roomName: null, forWhom: null, expiresAt: null,
      } })
      return
    }
    setPhase({ kind: 'loading' })
    lookupHandover(code)
      .then(async (view) => {
        if (view.state === 'expired') {
          // See the header: the scan is what releases a lapsed hold.
          try { await collectHandover(code) } catch { /* the sentence below still holds */ }
        }
        if (live) setPhase({ kind: 'view', view, justCollected: false })
      })
      .catch(() => live && setPhase({ kind: 'error' }))
    return () => { live = false }
  }, [code, attempt])

  async function confirm() {
    if (busy) return
    setBusy(true)
    try {
      const view = await collectHandover(code)
      setPhase({ kind: 'view', view, justCollected: view.state === 'collected' })
    } catch {
      setPhase({ kind: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="signin collect">
      <div className="signin__card collect__card">
        <img className="signin__logo" src={logo} alt="Conetec" width={140} />
        <CollectBody
          phase={phase}
          lang={lang}
          busy={busy}
          onConfirm={confirm}
          onRetry={() => setAttempt((n) => n + 1)}
        />
        <div className="signin__langs" role="group" aria-label={SIGN_IN_COPY[lang].languageLabel}>
          {SIGN_IN_LANGS.map((l) => (
            <button
              key={l.key}
              type="button"
              className="btn btn--link"
              lang={l.htmlLang}
              aria-pressed={lang === l.key}
              onClick={() => setLang(l.key)}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>
    </main>
  )
}
